import { createContext, useContext, useMemo } from "react";
import { ChallengeStage } from "@/services/challenges/types/challenges.types";
import { getRealityStages } from "@/services/challenges";
import { useUser } from "./UserContext";
import { useAuthenticatedQuery } from "@/hooks/useAuthenticatedQuery";
import { isBefore } from "date-fns";

type StagesContext = {
  stages: ChallengeStage[];
  currentStage?: ChallengeStage;
  currentStageNumber: number;
  isLoading: boolean;
  isValidStage: boolean;
  isStageClosed: boolean;
  isUserOutOfReality?: boolean;
  finalistIds: number[];
  isSemiFinalist?: boolean;
};

const StagesContext = createContext<StagesContext>({} as StagesContext);

type StagesProviderProps = {
  children: React.ReactNode;
};

export const StagesProvider = ({ children }: StagesProviderProps) => {
  const { userInfo } = useUser();
  const { data: stages, isLoading } = useAuthenticatedQuery(
    ["reality-stages", userInfo?.id?.toString() ?? ""],
    () => getRealityStages(),
    { retry: true, enabled: !!userInfo?.id }
  );

  const userStages = useMemo(() => {
    if (!stages) {
      return;
    }

    const sortedStages = stages.sort(
      (a, b) => new Date(a.endDate).getTime() - new Date(b.endDate).getTime()
    );

    return sortedStages;
  }, [stages]);

  const currentStage = useMemo(() => {
    if (!userStages) {
      return;
    }

    return userStages[userStages.length - 1];
  }, [userStages]);

  const currentStageNumber = useMemo(() => {
    return userStages?.length ?? -1;
  }, [userStages]);

  const isValidStage = useMemo(() => {
    if (!currentStage) return true;

    return !isBefore(currentStage.endDate, new Date());
  }, [currentStage]);

  const isStageClosed = useMemo(() => {
    return !!currentStage?.isClosed;
  }, [currentStage?.isClosed]);

  //this is only to avoid showing the dialog when the user is a semi finalist
  //TODO: remove this variable
  const isSemiFinalist = useMemo(
    () => !!(userStages?.length && userStages?.length >= 2),
    [userStages?.length]
  );

  /**
   * Participante "fora" após classificação: etapa atual encerrada, não é etapa final, e
   * (janela por data já passou OU ainda está só na primeira etapa após fechamento — não avançou).
   * Sem o segundo caso, `endDate` no futuro deixava `isValidStage` true e a UI nunca mostrava eliminação.
   */
  const isUserOutOfReality = useMemo(() => {
    if (isLoading) {
      return undefined;
    }
    if (!currentStage) {
      return undefined;
    }
    if (currentStage.isFinalStage) {
      return false;
    }
    if (!isStageClosed) {
      return false;
    }
    if (!isValidStage) {
      return true;
    }
    if (userStages && userStages.length === 1) {
      return true;
    }
    return false;
  }, [
    isLoading,
    isStageClosed,
    isValidStage,
    currentStage,
    userStages,
  ]);

  return (
    <StagesContext.Provider
      value={{
        stages: stages ?? [],
        currentStage,
        currentStageNumber,
        isValidStage,
        isStageClosed,
        isLoading,
        isUserOutOfReality,
        finalistIds: [],
        isSemiFinalist,
      }}
    >
      {children}
    </StagesContext.Provider>
  );
};

export const useStages = (): StagesContext => useContext(StagesContext);
